/**
 * The DSL's vocabulary, generated from the `@dslOperation` annotations on
 * src/dsl/grammar.ts.
 *
 * DO NOT EDIT. Documentation belongs in scripts/generate-dsl-grammar.ts, which
 * emits it — a comment written here is lost the next time this runs.
 */

/**
 * What one argument of a DSL operation accepts.
 *
 * The distinction that matters is `field` versus `value`: a `field` argument
 * names another field and is resolved against the record, a `value` argument is
 * a literal. Getting them the wrong way round is the commonest DSL mistake, and
 * this is what lets the editor catch it.
 */
export type DslArgumentType =
  | 'field'
  | 'value'
  | 'expr'
  | 'code'
  | 'date'
  | 'datetime'
  | 'lower'
  | 'upper'
  | 'maximum'
  | 'expression'
  | 'stable';

/** One argument of an operation: its name, its type, and what it is for. */
export interface DslArgumentDefinition {
  name: string;
  type: DslArgumentType;
  description: string;
}

interface BaseOperationDefinition {
  category: 'field' | 'object' | 'dates';
  detail: string;
  description: string;
  insertText: string;
  args: readonly DslArgumentDefinition[];
  allowOptions?: boolean;
}

/** An operation applying to a single field's value. */
export interface DslFieldOperationDefinition extends BaseOperationDefinition {
  category: 'field';
  name:
    | 'gte'
    | 'gt'
    | 'lte'
    | 'lt'
    | 'eq'
    | 'neq'
    | 'lteDateOnly'
    | 'gteDateOnly'
    | 'within'
    | 'withinHours'
    | 'withinMinutes'
    | 'requiredIf'
    | 'requiredIfValue'
    | 'notFutureDate'
    | 'refine';
  instruction:
    | {
        kind: 'cmp';
        op:
          | 'gte'
          | 'gt'
          | 'lte'
          | 'lt'
          | 'lteDateOnly'
          | 'gteDateOnly'
          | 'eq'
          | 'neq';
      }
    | { kind: 'within' }
    | { kind: 'withinHours' }
    | { kind: 'withinMinutes' }
    | { kind: 'requiredIf' }
    | { kind: 'requiredIfValue' }
    | { kind: 'notFutureDate' }
    | { kind: 'refine' };
  variadicArgs?: boolean;
}

/** An operation comparing fields with each other, and so belonging to the record rather than to a field. */
export interface DslObjectOperationDefinition extends BaseOperationDefinition {
  category: 'object';
  name: 'refine';
  instruction: { kind: 'objectRefine' };
  variadicArgs?: boolean;
}

/** An operation over dates and times, kept apart because date comparison has its own precision rules. */
export interface DslDatesOperationDefinition extends BaseOperationDefinition {
  category: 'dates';
  name: 'notFuture';
  instruction: { kind: 'datesNotFuture' };
  variadicArgs: boolean;
}

const FIELD_OPERATIONS = [
  {
    name: 'gte',
    category: 'field',
    detail:
      'Require field to be less than or equal to another field (FieldName <= OtherField)',
    description:
      'Ensures FieldName does not exceed OtherField. Validates: FieldName <= OtherField. Note: Name derives from constraint attachment perspective.',
    insertText:
      "field('FieldName').gte('OtherField') // FieldName <= OtherField",
    args: [
      {
        name: 'otherField',
        type: 'field',
        description: 'Field that serves as the upper bound.',
      },
    ],
    instruction: {
      kind: 'cmp',
      op: 'gte',
    },
    allowOptions: true,
    variadicArgs: false,
  },
  {
    name: 'gt',
    category: 'field',
    detail:
      'Require field to be strictly less than another field (FieldName < OtherField)',
    description:
      'Ensures FieldName is below OtherField. Validates: FieldName < OtherField. Note: Name derives from constraint attachment perspective.',
    insertText: "field('FieldName').gt('OtherField') // FieldName < OtherField",
    args: [
      {
        name: 'otherField',
        type: 'field',
        description: 'Field that serves as the strict upper bound.',
      },
    ],
    instruction: {
      kind: 'cmp',
      op: 'gt',
    },
    allowOptions: true,
    variadicArgs: false,
  },
  {
    name: 'lte',
    category: 'field',
    detail:
      'Require field to be greater than or equal to another field (FieldName >= OtherField)',
    description:
      'Ensures FieldName meets or exceeds OtherField. Validates: FieldName >= OtherField. Note: Name derives from constraint attachment perspective.',
    insertText:
      "field('FieldName').lte('OtherField') // FieldName >= OtherField",
    args: [
      {
        name: 'otherField',
        type: 'field',
        description: 'Field that serves as the lower bound.',
      },
    ],
    instruction: {
      kind: 'cmp',
      op: 'lte',
    },
    allowOptions: true,
    variadicArgs: false,
  },
  {
    name: 'lt',
    category: 'field',
    detail:
      'Require field to be strictly greater than another field (FieldName > OtherField)',
    description:
      'Ensures FieldName exceeds OtherField. Validates: FieldName > OtherField. Note: Name derives from constraint attachment perspective.',
    insertText: "field('FieldName').lt('OtherField') // FieldName > OtherField",
    args: [
      {
        name: 'otherField',
        type: 'field',
        description: 'Field that serves as the strict lower bound.',
      },
    ],
    instruction: {
      kind: 'cmp',
      op: 'lt',
    },
    allowOptions: true,
    variadicArgs: false,
  },
  {
    name: 'eq',
    category: 'field',
    detail: 'Ensure two fields or a field and value are equal',
    description:
      'Compare FieldName to OtherField or literal value for equality.',
    insertText: "field('FieldName').eq('OtherField')",
    args: [
      {
        name: 'otherField',
        type: 'field',
        description: 'Field or value to compare against.',
      },
    ],
    instruction: {
      kind: 'cmp',
      op: 'eq',
    },
    allowOptions: true,
    variadicArgs: false,
  },
  {
    name: 'neq',
    category: 'field',
    detail: 'Ensure two fields or a field and value are not equal',
    description:
      'Compare FieldName to OtherField or literal value for inequality.',
    insertText: "field('FieldName').neq('OtherField')",
    args: [
      {
        name: 'otherField',
        type: 'field',
        description: 'Field or value to compare against.',
      },
    ],
    instruction: {
      kind: 'cmp',
      op: 'neq',
    },
    allowOptions: true,
    variadicArgs: false,
  },
  {
    name: 'lteDateOnly',
    category: 'field',
    detail: 'Ensure the left date is not after the right date (date-only)',
    description:
      'Compare two date-only values and flag when FieldName occurs after OtherField.',
    insertText: "field('FieldName').lteDateOnly('OtherField')",
    args: [
      {
        name: 'otherField',
        type: 'field',
        description: 'Date field to compare against.',
      },
    ],
    instruction: {
      kind: 'cmp',
      op: 'lteDateOnly',
    },
    allowOptions: true,
    variadicArgs: false,
  },
  {
    name: 'gteDateOnly',
    category: 'field',
    detail: 'Ensure the left date is strictly after the right date (date-only)',
    description:
      'Compare two date-only values and flag when FieldName is not after OtherField.',
    insertText: "field('FieldName').gteDateOnly('OtherField')",
    args: [
      {
        name: 'otherField',
        type: 'field',
        description: 'Date field to compare against.',
      },
    ],
    instruction: {
      kind: 'cmp',
      op: 'gteDateOnly',
    },
    allowOptions: true,
    variadicArgs: false,
  },
  {
    name: 'within',
    category: 'field',
    detail: 'Ensure a field falls within the inclusive bounds provided',
    description:
      'Assert that FieldName stays between MinField and MaxField (inclusive).',
    insertText: "field('FieldName').within('MinField','MaxField')",
    args: [
      {
        name: 'minField',
        type: 'field',
        description: 'Lower bound field.',
      },
      {
        name: 'maxField',
        type: 'field',
        description: 'Upper bound field.',
      },
    ],
    instruction: {
      kind: 'within',
    },
    allowOptions: true,
    variadicArgs: false,
  },
  {
    name: 'withinHours',
    category: 'field',
    detail: 'Ensure two datetime fields are within N hours of each other',
    description:
      'Validates that the time difference between FieldName and OtherField does not exceed the specified number of hours.',
    insertText: "field('FieldName').withinHours('OtherField', 24)",
    args: [
      {
        name: 'otherField',
        type: 'field',
        description: 'Datetime field to compare against.',
      },
      {
        name: 'hours',
        type: 'value',
        description: 'Maximum number of hours difference allowed.',
      },
    ],
    instruction: {
      kind: 'withinHours',
    },
    allowOptions: true,
    variadicArgs: false,
  },
  {
    name: 'withinMinutes',
    category: 'field',
    detail: 'Ensure two datetime fields are within N minutes of each other',
    description:
      'Validates that the time difference between FieldName and OtherField does not exceed the specified number of minutes.',
    insertText: "field('FieldName').withinMinutes('OtherField', 30)",
    args: [
      {
        name: 'otherField',
        type: 'field',
        description: 'Datetime field to compare against.',
      },
      {
        name: 'minutes',
        type: 'value',
        description: 'Maximum number of minutes difference allowed.',
      },
    ],
    instruction: {
      kind: 'withinMinutes',
    },
    allowOptions: true,
    variadicArgs: false,
  },
  {
    name: 'requiredIf',
    category: 'field',
    detail: 'Mark a field as required when another field is present',
    description: 'Require FieldName when OtherField has a value.',
    insertText: "field('FieldName').requiredIf('OtherField')",
    args: [
      {
        name: 'otherField',
        type: 'field',
        description: 'Field whose presence should trigger the requirement.',
      },
    ],
    instruction: {
      kind: 'requiredIf',
    },
    allowOptions: true,
    variadicArgs: false,
  },
  {
    name: 'requiredIfValue',
    category: 'field',
    detail: 'Require a field when another field matches a value',
    description: 'Require FieldName when OtherField equals the supplied Value.',
    insertText: "field('FieldName').requiredIfValue('OtherField','Value')",
    args: [
      {
        name: 'otherField',
        type: 'field',
        description: 'Field to inspect.',
      },
      {
        name: 'value',
        type: 'value',
        description: 'Value that triggers the requirement.',
      },
    ],
    instruction: {
      kind: 'requiredIfValue',
    },
    allowOptions: true,
    variadicArgs: false,
  },
  {
    name: 'notFutureDate',
    category: 'field',
    detail: 'Ensure the date-only value is not in the future',
    description: 'Validate that FieldName (a date) is today or earlier.',
    insertText: "field('FieldName').notFutureDate()",
    args: [],
    instruction: {
      kind: 'notFutureDate',
    },
    allowOptions: true,
    variadicArgs: false,
  },
  {
    name: 'refine',
    category: 'field',
    detail: 'Run a custom expression for a specific field',
    description: 'Attach a bespoke expression-based rule to FieldName.',
    insertText: "field('FieldName').refine('expr(root.FieldName)')",
    args: [
      {
        name: 'expression',
        type: 'expr',
        description: 'Expression to evaluate against the root object.',
      },
    ],
    instruction: {
      kind: 'refine',
    },
    allowOptions: true,
    variadicArgs: false,
  },
] as const satisfies readonly DslFieldOperationDefinition[];

const OBJECT_OPERATIONS = [
  {
    name: 'refine',
    category: 'object',
    detail: 'Apply a schema-level refinement with a code and expression',
    description:
      'Define a schema-wide check identified by RuleCode using an expression.',
    insertText: "object.refine('RuleCode','expr(root)')",
    args: [
      {
        name: 'code',
        type: 'code',
        description: 'Stable identifier for the refinement.',
      },
      {
        name: 'expression',
        type: 'expr',
        description: 'Expression evaluated against the root object.',
      },
    ],
    instruction: {
      kind: 'objectRefine',
    },
    allowOptions: true,
    variadicArgs: false,
  },
] as const satisfies readonly DslObjectOperationDefinition[];

const DATE_OPERATIONS = [
  {
    name: 'notFuture',
    category: 'dates',
    detail: 'Assert a group of dates are not in the future',
    description: 'Ensure each listed date is today or earlier.',
    insertText: "dates('StartDate','EndDate').notFuture()",
    args: [],
    instruction: {
      kind: 'datesNotFuture',
    },
    allowOptions: true,
    variadicArgs: true,
  },
] as const satisfies readonly DslDatesOperationDefinition[];

/** Every DSL operation, grouped by what it applies to. */
export interface DslGrammarDefinition {
  fieldOperations: readonly DslFieldOperationDefinition[];
  objectOperations: readonly DslObjectOperationDefinition[];
  dateOperations: readonly DslDatesOperationDefinition[];
}

/**
 * The DSL's vocabulary, as data.
 *
 * Read by the parser, by the editor's completion and hover, and by the generated
 * grammar documentation — which is the point of it being data rather than code.
 * Adding an operation makes it real everywhere at once.
 */
export const DSL_GRAMMAR: DslGrammarDefinition = {
  fieldOperations: FIELD_OPERATIONS,
  objectOperations: OBJECT_OPERATIONS,
  dateOperations: DATE_OPERATIONS,
};

/** Every field operation name, derived from DSL_GRAMMAR so the type cannot drift from the data. */
export type FieldOperationName = (typeof FIELD_OPERATIONS)[number]['name'];
/** Every object operation name, derived from DSL_GRAMMAR. */
export type ObjectOperationName = (typeof OBJECT_OPERATIONS)[number]['name'];
/** Every date operation name, derived from DSL_GRAMMAR. */
export type DateOperationName = (typeof DATE_OPERATIONS)[number]['name'];

/** A field operation by name. Throws for an unknown one — the parameter type makes that a compile error, so a throw here means a cast went wrong. Use findFieldOperation for a name from source text. */
export function getFieldOperation(
  name: FieldOperationName,
): DslFieldOperationDefinition {
  const op = FIELD_OPERATIONS.find((def) => def.name === name);
  if (!op) {
    throw new Error(`Unknown field operation: ${name}`);
  }
  return op;
}

/** A field operation by name, or undefined. The right one for a name that came from user input rather than from code. */
export function findFieldOperation(
  name: string,
): DslFieldOperationDefinition | undefined {
  return FIELD_OPERATIONS.find((def) => def.name === name);
}

/** An object operation by name, or undefined. */
export function findObjectOperation(
  name: string,
): DslObjectOperationDefinition | undefined {
  return OBJECT_OPERATIONS.find((def) => def.name === name);
}

/** A date operation by name, or undefined. */
export function findDateOperation(
  name: string,
): DslDatesOperationDefinition | undefined {
  return DATE_OPERATIONS.find((def) => def.name === name);
}
